import React, { useState } from 'react';
import { Card } from '../../../design-system/Card';
import { Button } from '../../../design-system/Button';
import { Badge } from '../../../design-system/Badge';
import { Tabs } from '../../../design-system/Tabs';
import { Modal } from '../../../design-system/Modal';
import { Input } from '../../../design-system/Input';
import {
  Film,
  Layers,
  Users,
  Gamepad2,
  Package,
  Plus,
  Sparkles,
  Swords
} from 'lucide-react';

export const GameDevView = () => {
  const [activeSubTab, setActiveSubTab] = useState('storyboard');
  const [isNewAssetModalOpen, setIsNewAssetModalOpen] = useState(false);
  const [isNewLevelModalOpen, setIsNewLevelModalOpen] = useState(false);

  const [assetName, setAssetName] = useState('');
  const [assetType] = useState('3D Model');
  const [polyBudget, setPolyBudget] = useState('25k polys');

  const [levelName, setLevelName] = useState('');
  const [levelDesigner, setLevelDesigner] = useState('');

  const tabs = [
    { id: 'storyboard', label: 'Storyboards & GDD', icon: <Film size={15} /> },
    { id: 'levels', label: 'Level Flow & Environments', icon: <Layers size={15} /> },
    { id: 'codex', label: 'Characters & Weapons Codex', icon: <Users size={15} /> },
    { id: 'assets', label: 'Asset Pipeline & VFX', icon: <Package size={15} /> },
    { id: 'builds', label: 'Builds & Playtest QA', icon: <Gamepad2 size={15} /> }
  ];

  const [storyboardShots] = useState([
    {
      id: 'shot_1',
      scene: 'Scene 04 • Shot 1',
      duration: '4.2s',
      title: 'Alleyway Drone Ambush',
      camera: 'Low Angle Pan',
      audio: 'Rain SFX + Cyberpunk Synth Drone',
      desc: 'Camera pans low angle following protagonist as neon reflections flicker on wet pavement.'
    },
    {
      id: 'shot_2',
      scene: 'Scene 04 • Shot 2',
      duration: '2.8s',
      title: 'EMP Gauntlet Countermeasure',
      camera: 'Extreme Close-Up',
      audio: 'High-Pitch EMP Charge Whine',
      desc: 'Extreme close-up on cybernetic gauntlet activating blue electrical surge, disabling attack drones.'
    },
    {
      id: 'shot_3',
      scene: 'Scene 05 • Shot 1',
      duration: '5.5s',
      title: 'NeuroCorp Tower Infiltration',
      camera: 'Establishing High-Altitude Crane',
      audio: 'Thunder + Distant City Ambience',
      desc: 'Protagonist grapples onto 80th floor balcony overlooking neon metropolis.'
    }
  ]);

  const [levels, setLevels] = useState([
    { id: 'lvl_1', name: 'Sector 7: Underbelly Metro', designer: 'Alex Rivera', status: 'Playtest Ready', progress: 90 },
    { id: 'lvl_2', name: 'Sector 8: Highrise Penthouse', designer: 'Elena Rostov', status: 'Greybox Pass', progress: 60 },
    { id: 'lvl_3', name: 'NeuroCorp Central Vault', designer: 'Marcus Vance', status: 'Concept / Blockout', progress: 30 }
  ]);

  const [assets, setAssets] = useState([
    { id: 'ast_1', name: 'Protagonist Cyber Gauntlet (LOD 0-3)', type: '3D Weapon Model', budget: '18,500 polys', status: 'Ready for Rigging' },
    { id: 'ast_2', name: 'NeuroCorp Security Enforcer Mech', type: '3D Character Mesh', budget: '42,000 polys', status: 'Texturing in Substance' },
    { id: 'ast_3', name: 'Plasma Blast Particle Shader', type: 'Niagara VFX', budget: '60 FPS Target', status: 'Engine Integrated' }
  ]);

  const handleAddAsset = (e) => {
    e?.preventDefault();
    if (!assetName.trim()) return;

    setAssets([
      ...assets,
      {
        id: 'ast_' + Date.now(),
        name: assetName.trim(),
        type: assetType,
        budget: polyBudget,
        status: 'In Production'
      }
    ]);
    setAssetName('');
    setIsNewAssetModalOpen(false);
  };

  const handleAddLevel = (e) => {
    e?.preventDefault();
    if (!levelName.trim()) return;

    setLevels([
      ...levels,
      {
        id: 'lvl_' + Date.now(),
        name: levelName.trim(),
        designer: levelDesigner.trim() || 'Lead Designer',
        status: 'Concept / Blockout',
        progress: 20
      }
    ]);
    setLevelName('');
    setLevelDesigner('');
    setIsNewLevelModalOpen(false);
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }} className="animate-fade-in">
      {/* Module Banner */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <Badge variant="purple" size="sm">Specialized Studio Methodology</Badge>
            <h2 style={{ fontSize: '20px', fontWeight: '800' }}>Game Development Studio</h2>
          </div>
          <p style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>
            Cinematics, visual storyboards, level blockouts, weapon codex, asset budget pipeline, and playtests
          </p>
        </div>

        <Tabs tabs={tabs} activeTab={activeSubTab} onChange={setActiveSubTab} />
      </div>

      {/* TAB 1: STORYBOARDS & GDD */}
      {activeSubTab === 'storyboard' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' }}>
            {storyboardShots.map((shot) => (
              <Card key={shot.id} padding="20px">
                <div
                  style={{
                    height: '140px',
                    backgroundColor: 'var(--surface-recessed)',
                    borderRadius: 'var(--radius-md)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '12px',
                    border: '1px solid var(--border-subtle)'
                  }}
                >
                  <Film size={32} color="var(--accent-teal)" />
                  <span style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '6px' }}>Shot Visualization Canvas</span>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                  <Badge variant="teal" size="xs">{shot.scene}</Badge>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontWeight: '700' }}>{shot.duration}</span>
                </div>

                <h4 style={{ fontSize: '15px', fontWeight: '800', marginBottom: '4px' }}>{shot.title}</h4>
                <p style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: '1.5', marginBottom: '10px' }}>
                  {shot.desc}
                </p>

                <div style={{ backgroundColor: 'var(--surface-recessed)', padding: '8px 10px', borderRadius: 'var(--radius-sm)', fontSize: '11px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                  <div><strong>Camera:</strong> {shot.camera}</div>
                  <div><strong>Audio:</strong> {shot.audio}</div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* TAB 2: LEVEL PLANNING */}
      {activeSubTab === 'levels' && (
        <Card padding="24px">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div>
              <h3 style={{ fontSize: '16px', fontWeight: '800' }}>Level Flow & Environment Progression</h3>
              <p style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Track environment blockout stages to gold master</p>
            </div>
            <Button variant="primary" size="sm" icon={<Plus size={14} />} onClick={() => setIsNewLevelModalOpen(true)}>
              New Level
            </Button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {levels.map((lvl) => (
              <div
                key={lvl.id}
                style={{
                  padding: '14px 16px',
                  backgroundColor: 'var(--surface-recessed)',
                  borderRadius: 'var(--radius-md)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: '14px',
                  border: '1px solid var(--border-subtle)'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                  <Layers size={20} color="var(--accent-teal)" />
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: '800' }}>{lvl.name}</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
                      Lead: {lvl.designer} • Completion: {lvl.progress}%
                    </div>
                  </div>
                </div>

                <Badge
                  variant={lvl.status === 'Playtest Ready' ? 'success' : lvl.status === 'Greybox Pass' ? 'teal' : 'neutral'}
                  size="sm"
                >
                  {lvl.status}
                </Badge>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* TAB 3: CHARACTERS & WEAPONS CODEX */}
      {activeSubTab === 'codex' && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' }}>
          {/* Character 1 */}
          <Card padding="20px">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
              <div style={{ width: '44px', height: '44px', borderRadius: 'var(--radius-md)', backgroundColor: 'var(--surface-recessed)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Users size={22} color="var(--accent-teal)" />
              </div>
              <div>
                <h4 style={{ fontSize: '16px', fontWeight: '800' }}>Kaelen Voss</h4>
                <Badge variant="teal" size="xs">Protagonist • Operative</Badge>
              </div>
            </div>
            <p style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.5', marginBottom: '12px' }}>
              Ex-corporate cybernetics operative seeking vengeance against the NeuroCorp syndicate.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', fontSize: '11px', backgroundColor: 'var(--surface-recessed)', padding: '8px 10px', borderRadius: 'var(--radius-sm)' }}>
              <div><strong>HP Base:</strong> 450</div>
              <div><strong>Speed:</strong> 6.8 m/s</div>
              <div><strong>Special:</strong> EMP Gauntlet Surge</div>
              <div><strong>Faction:</strong> The Undercity</div>
            </div>
          </Card>

          {/* Weapon 1 */}
          <Card padding="20px">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
              <div style={{ width: '44px', height: '44px', borderRadius: 'var(--radius-md)', backgroundColor: 'var(--surface-recessed)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Swords size={22} color="var(--color-purple)" />
              </div>
              <div>
                <h4 style={{ fontSize: '16px', fontWeight: '800' }}>Vortex Plasma Repeater</h4>
                <Badge variant="purple" size="xs">Heavy Energy Weapon</Badge>
              </div>
            </div>
            <p style={{ fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.5', marginBottom: '12px' }}>
              High-rate plasma fire causing ion buildup and localized explosive discharge.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '6px', fontSize: '11px', backgroundColor: 'var(--surface-recessed)', padding: '8px 10px', borderRadius: 'var(--radius-sm)' }}>
              <div><strong>DPS:</strong> 340</div>
              <div><strong>Fire Rate:</strong> 720 RPM</div>
              <div><strong>Ammo Type:</strong> Plasma Cell</div>
              <div><strong>Tier:</strong> Legendary</div>
            </div>
          </Card>
        </div>
      )}

      {/* TAB 4: ASSET PIPELINE */}
      {activeSubTab === 'assets' && (
        <Card padding="24px">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div>
              <h3 style={{ fontSize: '16px', fontWeight: '800' }}>Asset Registry & Polygon Budgets</h3>
              <p style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>Track 3D assets, textures, and Niagara particle systems</p>
            </div>
            <Button variant="primary" size="sm" icon={<Plus size={14} />} onClick={() => setIsNewAssetModalOpen(true)}>
              Register Asset
            </Button>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {assets.map((ast) => (
              <div
                key={ast.id}
                style={{
                  padding: '12px 16px',
                  backgroundColor: 'var(--surface-recessed)',
                  borderRadius: 'var(--radius-md)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  border: '1px solid var(--border-subtle)'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <Package size={18} color="var(--accent-teal)" />
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: '700' }}>{ast.name}</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
                      Type: {ast.type} • Budget: {ast.budget}
                    </div>
                  </div>
                </div>
                <Badge variant="teal" size="xs">{ast.status}</Badge>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* TAB 5: BUILDS & PLAYTEST QA */}
      {activeSubTab === 'builds' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
          <Card padding="20px">
            <h3 style={{ fontSize: '16px', fontWeight: '800', marginBottom: '12px' }}>Unreal Engine 5 Package Releases</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div style={{ padding: '14px', backgroundColor: 'var(--surface-recessed)', borderRadius: 'var(--radius-md)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: '700' }}>v0.4.12-alpha (Windows DX12 / Steam Deck)</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Commit: 7a9e14 • Packaged: Aug 21, 2026</div>
                </div>
                <Badge variant="success" size="sm">Playtest Passed ✓</Badge>
              </div>
            </div>
          </Card>

          <Card padding="20px">
            <h3 style={{ fontSize: '16px', fontWeight: '800', marginBottom: '12px' }}>Playtest QA Feedback Log</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {[
                { tester: 'Alex (Combat QA)', feedback: 'Gauntlet EMP cooldown feels 1.5s too long during Sector 7 subway fight.', severity: 'Tuning', status: 'Fixed' },
                { tester: 'Sarah (Design QA)', feedback: 'Framerate drop near highrise neon holographic waterfall on GTX 3070.', severity: 'Performance', status: 'Investigating' }
              ].map((fb, idx) => (
                <div key={idx} style={{ padding: '10px 14px', backgroundColor: 'var(--surface-recessed)', borderRadius: 'var(--radius-md)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ fontSize: '13px', fontWeight: '600' }}>{fb.feedback}</div>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)' }}>Logged by {fb.tester}</div>
                  </div>
                  <Badge variant={fb.status === 'Fixed' ? 'success' : 'warning'} size="xs">{fb.status}</Badge>
                </div>
              ))}
            </div>
          </Card>
        </div>
      )}

      {/* NEW ASSET MODAL */}
      <Modal
        isOpen={isNewAssetModalOpen}
        onClose={() => setIsNewAssetModalOpen(false)}
        title="Register 3D Asset or VFX"
        subtitle="Specify budget thresholds and engine integration target"
        footer={
          <>
            <Button variant="ghost" onClick={() => setIsNewAssetModalOpen(false)}>Cancel</Button>
            <Button variant="primary" onClick={handleAddAsset} icon={<Sparkles size={14} />}>Save Asset</Button>
          </>
        }
      >
        <form onSubmit={handleAddAsset} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <Input
            label="Asset Name *"
            placeholder="e.g., Cyberpunk Hovercar Vehicle Mesh"
            value={assetName}
            onChange={(e) => setAssetName(e.target.value)}
            required
            autoFocus
          />
          <Input
            label="Poly / Memory Budget"
            placeholder="e.g., 35k polys, 2K textures"
            value={polyBudget}
            onChange={(e) => setPolyBudget(e.target.value)}
          />
        </form>
      </Modal>

      {/* NEW LEVEL MODAL */}
      <Modal
        isOpen={isNewLevelModalOpen}
        onClose={() => setIsNewLevelModalOpen(false)}
        title="Create Game Level Flow"
        subtitle="Initialize environment blockout and assign lead designer"
        footer={
          <>
            <Button variant="ghost" onClick={() => setIsNewLevelModalOpen(false)}>Cancel</Button>
            <Button variant="primary" onClick={handleAddLevel} icon={<Sparkles size={14} />}>Create Level</Button>
          </>
        }
      >
        <form onSubmit={handleAddLevel} style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <Input
            label="Level Name *"
            placeholder="e.g., Sector 9: Industrial Chemical Plant"
            value={levelName}
            onChange={(e) => setLevelName(e.target.value)}
            required
            autoFocus
          />
          <Input
            label="Lead Level Designer"
            placeholder="e.g., Alex Rivera"
            value={levelDesigner}
            onChange={(e) => setLevelDesigner(e.target.value)}
          />
        </form>
      </Modal>
    </div>
  );
};
